import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import ActionMenu, { LinkAction } from "widgets/ActionMenu";
import { INCOMPLETE_DVAST_TOOLTIP, IMPOSIUM_URL, INCOMPLETE_IMPOSIUM_TOOLTIP } from "utils/constants";
import {
	isAdstudioCreative,
	isSocialCreative,
	transcodingChecks,
	isIncomplete,
	isVASTAudioCreative,
	isSocialPreviewDisabled,
	isSocialEditDisabled,
	isDisplayHTMLTagCreative,
	isDynamic,
	isVideoBuilderCreative,
	isImposiumValid
} from "utils/common";
import {
	isDVAST,
	isPartnerVAST,
	isProduction,
	isRejected
} from "utils/CreativeActions";

import  CREATIVE_ACTION_LISTS from "./CreativeActionLists";
import { EditPartnerVAST } from "./CreativeActionLists/VASTActions";

import { getAutoClassificationEnabled } from "selectors/Intelligence";
import { getAiVersionGenerationEnabled } from "selectors/AI";

import AdStudioLink from "widgets/actions/AdStudioLink";
import ImposiumLink from "widgets/actions/ImposiumAction";
import { TRANSCODED_DVAST_ELLIPSIS_ACTION_LIST } from "widgets/grid/cells/CreativeActionLists/EllipsisActions";

import "./CreativeActions.css";

const {
	PREVIEW_ACTION_LIST,
	SOCIAL_PREVIEW_ACTION_LIST,
	APPROVAL_ACTION_LIST,
	VERSION_ACTION_LIST,
	EDIT_SOCIAL_ACTION_LIST,
	ELLIPSIS_ACTION_LIST,
	TRANSCODING_ELLIPSIS_ACTION_LIST,
	TRANSCODED_ELLIPSIS_ACTION_LIST,
} = CREATIVE_ACTION_LISTS;

const commonLinkActionProps={
	useMenuItem:false,
	className:"action-link",
	iconClass:"bounce-circle",
};

export function CreativeActions(props) {
	let { dependentValues } = props;
	let user = useSelector(state => state.you);
	const isAutoClassificationEnabled = useSelector(getAutoClassificationEnabled);
	const isAiVersionGenerationEnabled = useSelector(getAiVersionGenerationEnabled);

	// ***** Boolean Display Functions ***** //
	let isSocialAd = isSocialCreative({ adType:dependentValues?.adType })
		, isDynamicVast = isDVAST(dependentValues?.adType)
		, isTranscoding  = transcodingChecks.transcoding(dependentValues)
		, isTranscoded  = transcodingChecks.transcoded(dependentValues)
		, isTranscodedError  = transcodingChecks.error(dependentValues)
		, isAdStudio = isAdstudioCreative(dependentValues)
		, isStatusIncomplete = isIncomplete(dependentValues)
		, isVASTAudio = isVASTAudioCreative(dependentValues)
		, isIncompleteDynamicVast = isDynamicVast && isStatusIncomplete
		, isCarouselEditDisabled = isSocialEditDisabled(dependentValues)
		, isCarouselPreviewDisabled = isSocialPreviewDisabled(dependentValues)
		, isDisplayTag = isDisplayHTMLTagCreative(dependentValues)
		, isDynamicCreative = isDynamic(dependentValues)
		, isVASTPartner = isPartnerVAST(dependentValues?.adType)
		, isImposium = isImposiumValid(dependentValues)
		, isIncompleteImposium = isImposium && isStatusIncomplete
		, isVideoBuilder = isVideoBuilderCreative(dependentValues)
		;

	const approvalTooltipContent = useMemo(() => {
		if (isIncompleteImposium) {
			return INCOMPLETE_IMPOSIUM_TOOLTIP;
		} else if(isIncompleteDynamicVast) {
			return INCOMPLETE_DVAST_TOOLTIP;
		} else if(isStatusIncomplete) {
			return "Approval workflow is disabled - <br /> creative is missing required assets";
		} else {
			return "Approval Workflow";
		}
	}, [isIncompleteDynamicVast, isStatusIncomplete, isIncompleteImposium]);

	const versionTargetingTooltipContent = useMemo(() => {
		if (isIncompleteImposium) {
			return INCOMPLETE_IMPOSIUM_TOOLTIP;
		} else if (isIncompleteDynamicVast) {
			return INCOMPLETE_DVAST_TOOLTIP;
		} else {
			return "Versions";
		}
	}, [isIncompleteDynamicVast, isIncompleteImposium]);

	// ***** Action Menu Props ***** //
	let actionMenuProps = {
		approval: {
			icon:"far fa-badge-check",
			isDisabled: isTranscoding || isStatusIncomplete || isVideoBuilder,
			actionLists: APPROVAL_ACTION_LIST,
			actionContext: { user, dependentValues },
			tooltipContent: approvalTooltipContent,
			isMultiLine: isStatusIncomplete,
		},
		preview: {
			icon:"far fa-eye",
			isDisabled:isTranscoding || isStatusIncomplete,
			actionLists: PREVIEW_ACTION_LIST,
			actionContext:{ user, dependentValues },
			tooltipContent: isStatusIncomplete ? "Previews are disabled - <br />creative is missing required assets" : "Preview",
			isMultiLine: isStatusIncomplete,
		},
		socialPreview: {
			icon:"far fa-eye",
			actionLists:SOCIAL_PREVIEW_ACTION_LIST,
			actionContext:{ user, dependentValues },
			isDisabled: isTranscoding || isStatusIncomplete || isCarouselPreviewDisabled,
			tooltipContent: isStatusIncomplete ? "Previews are disabled - <br />creative is missing required assets" : "Preview",
			isMultiLine: isStatusIncomplete,
		},
		dynamicVastPreview: {
			icon:"far fa-eye",
			actionLists: PREVIEW_ACTION_LIST,
			actionContext:{ user, dependentValues },
			tooltipContent: isStatusIncomplete ? "Previews are disabled - <br />creative is missing required assets" : "Preview",
			isMultiLine: isStatusIncomplete,
			isDisabled: isTranscoding || isStatusIncomplete,
		},
		versionTargeting: {
			icon:"far fa-bolt",
			actionLists:VERSION_ACTION_LIST,
			actionContext: { user, dependentValues, isAiVersionGenerationEnabled },
			tooltipContent: versionTargetingTooltipContent,
			isDisabled: isTranscoding || isIncompleteDynamicVast,
			isMultiLine: isIncompleteDynamicVast,
		},
		editSocial: {
			icon: "far fa-pencil",
			actionContext: { user, dependentValues },
			actionLists: EDIT_SOCIAL_ACTION_LIST,
			tooltipContent: "Edit Creative",
			isDisabled: isCarouselEditDisabled,
		},
		ellipsis: {
			icon:"moreHorizontal",
			actionLists: [ELLIPSIS_ACTION_LIST],
			actionContext: { user, dependentValues, isAutoClassificationEnabled },
			tooltipContent: "More Actions",
		},
	};

	// ***** Link Action Props ***** //
	let linkActionProps = {
		videoSpecs:{
			...commonLinkActionProps,
			iconName:"far fa-file-alt",
			isDisabled:isTranscoding,
			path:`/library/${dependentValues?.libraryId}/modal/video-specs/${dependentValues?.creativeId}/`,
			tooltipContent: "Video Specs",
		},
		audioSpecs:{
			...commonLinkActionProps,
			iconName:"far fa-file-alt",
			isDisabled:isTranscoding,
			path:`/library/${dependentValues?.libraryId}/modal/audio-specs/${dependentValues?.creativeId}/`,
			tooltipContent: "Audio Specs",
		},
		editSocialConcept:{
			...commonLinkActionProps,
			iconName:"far fa-eye",
			isDisabled:isTranscoding || isStatusIncomplete,
			path:`/library/${dependentValues?.libraryId}/modal/preview-creative/social/${dependentValues?.creativeId}/`,
			tooltipContent: isStatusIncomplete ? "Previews are disabled - <br />creative is missing required assets" : "Preview",
			isMultiLine: isStatusIncomplete,
		},
		editDisplayTag: {
			...commonLinkActionProps,
			iconName: "far fa-pencil",
			path: `modal/edit-concept/display-tag/${dependentValues?.creativeId}/`,
			tooltipContent: "Edit Creative Tag",
		},
		selectNewVideo:{
			...commonLinkActionProps,
			iconName:"far fa-video-plus",
			path:`modal/replace-vast/${dependentValues?.creativeId}/`,
			tooltipContent: "Select New Video",
		},
		selectNewAudio:{
			...commonLinkActionProps,
			iconName:"far fa-volume",
			path:`modal/replace-vast-audio/${dependentValues?.creativeId}/`,
			tooltipContent: "Select New Audio File",
		},
	};

	let  actionList = []
		, $ellipsis = {};

	// ***** Action Menus ***** //
	let $approval = <ActionMenu key="approval" {...actionMenuProps.approval} />
		, $preview = <ActionMenu key="preview" {...actionMenuProps.preview} />
		, $socialPreview = <ActionMenu key="preview" {...actionMenuProps.socialPreview} />
		, $dynamicVastPreview = <ActionMenu key="preview" {...actionMenuProps.dynamicVastPreview} />
		, $versionTargeting = <ActionMenu key="targeting" {...actionMenuProps.versionTargeting} />
		, $editSocial = <ActionMenu key="edit-social" {...actionMenuProps.editSocial} />
		;

	$ellipsis.base = <ActionMenu key="additional" {...actionMenuProps.ellipsis} actionLists={ELLIPSIS_ACTION_LIST} />;
	$ellipsis.transcoding = <ActionMenu key="additional" {...actionMenuProps.ellipsis} actionLists={ TRANSCODING_ELLIPSIS_ACTION_LIST } />;
	$ellipsis.transcoded = <ActionMenu key="additional" {...actionMenuProps.ellipsis} actionLists={ TRANSCODED_ELLIPSIS_ACTION_LIST } />;
	$ellipsis.dvast = <ActionMenu key="additional" {...actionMenuProps.ellipsis} actionLists={ TRANSCODED_DVAST_ELLIPSIS_ACTION_LIST } />;

	// ***** Link Actions ***** //
	let $videoSpecs =  (<LinkAction key="video-specs" {...linkActionProps.videoSpecs} />)
		, $audioSpecs =  (<LinkAction key="audio-specs" {...linkActionProps.audioSpecs} />)
		, $selectNewVideo = (<LinkAction key="select-new-video" {...linkActionProps.selectNewVideo} />)
		, $selectNewAudio = (<LinkAction key="select-new-audio" {...linkActionProps.selectNewAudio} />)
		, $openInAdStudio = (<AdStudioLink key="ad-studio-link" creativeId={dependentValues?.creativeId} isDisabled={isTranscoding} />)
		, $editDisplayTag = (<LinkAction key="edit-display-tag" {...linkActionProps.editDisplayTag} />)
		, $imposium = (
			<ImposiumLink
				key="imposium"
				url={`${IMPOSIUM_URL}/${dependentValues?.storyId}?organization_id=${dependentValues?.organizationId}`}
				tooltipContent="Open in Imposium"
				isDisabled={isTranscoding}
			/>
		)
		;

	let $specsModal = isVASTAudio ? $audioSpecs : $videoSpecs;
	let $selectVASTAction = isVASTAudio ? $selectNewAudio : $selectNewVideo;

	if (isVASTAudio) {
		// HOPI-1410 Open in Ad Studio is available for video but not audio
		$openInAdStudio = null;
	}

	if(isSocialAd){
		if (isDynamicCreative) {
			actionList = [
				$socialPreview
				, $approval
				, $editSocial
				, $versionTargeting
				, $ellipsis.base
			];
		} else {
			actionList = [
				$socialPreview
				, $approval
				, $editSocial
				, $ellipsis.base
			];
		}
	} else if (isDynamicVast) {
		if (isTranscodedError) {
			actionList = [
				$selectNewVideo,
				$videoSpecs,
				...isImposium ? [$imposium] : [],
				...isAdStudio ? [$openInAdStudio] : [],
				$ellipsis.transcoding,
			];
		} else if(isAdStudio){
			actionList = [
				$dynamicVastPreview
				, $approval
				, $versionTargeting
				, $openInAdStudio
				, $ellipsis.dvast
			];
		} else if (isImposium) {
			actionList = [
				$dynamicVastPreview
				, $approval
				, $versionTargeting
				, $imposium
				, $ellipsis.dvast
			];
		} else {
			actionList = [
				$dynamicVastPreview
				, $approval
				, $versionTargeting
				, $ellipsis.dvast
			];
		}
	} else if(isTranscodedError){  // HOPI-461 https://flashtalkingus.atlassian.net/browse/HOPI-461
		actionList = [
			$selectVASTAction
			, $specsModal
			, $openInAdStudio
			, $ellipsis.transcoding
		];
	} else if(isTranscoding){  // HOPI-456 https://flashtalkingus.atlassian.net/browse/HOPI-456
		actionList = [
			  $preview
			, $approval
			, $specsModal
			, $openInAdStudio
			, $ellipsis.transcoding
		];
	} else if(isTranscoded){  // HOPI-458 https://flashtalkingus.atlassian.net/browse/HOPI-458
		actionList = [
			  $preview
			, $approval
			, $specsModal
			, $openInAdStudio
			, $ellipsis.transcoded
		];
	} else if(isAdStudio) {
		actionList = [
			  $preview
			, $approval
			, $versionTargeting
			, $openInAdStudio
			, $ellipsis.base
		];
	} else if (isImposium) {
		actionList = [
			$preview
			, $approval
			, $versionTargeting
			, $imposium
			, $ellipsis.dvast
		];
	} else if(isDisplayTag) {
		actionList = [
			$preview
		  , $approval
		  , $editDisplayTag
		  , $ellipsis.base
	  	];
	} else if(isVASTPartner){
		let $editPartner = null;
		if(isProduction(dependentValues.approval) || isRejected(dependentValues.approval)){
			$editPartner = <EditPartnerVAST key="editVAST" {...dependentValues} />;
		}
		actionList = [
			  $preview
			, $approval
			, $editPartner
			, $ellipsis.base
		];
	} else {
		actionList = [
			  $preview
			, $approval
			, $versionTargeting
			, $ellipsis.base
		];
	}

	return <div className="grid-actions">{actionList}</div>;
}

function getActionMetadata(data) {
	let extraInfo = {
		libraryId:      data.creativeLibraryId,
		conceptId:      data.conceptID,
		conceptName:    data.conceptName,
		creativeId:     data.id,
		creativeName:   data.name,
		fileName:       data.filename,
		approval:       data.approval,
		status:         data.status,
		dynamicTarget:  data.dynamicTarget,
		adType:         data.adType,
		isDynamic:      data.isDynamic,
		videos:         data.videos || [],
		origin:         data.origin || false,
		openInAdstudio: data.openInAdstudio,
		summaryFileName:data.name,
		rejectNotes:    data.rejectNotes,
		numberOfVersions: data.numberOfVersions,
		vastTag:data.vastTag,
		storyId: data.storyId,
		organizationId: data.organizationId,
		html5Meta: data.html5Meta,
	};

	return extraInfo;
}


// TODO: Why do we need to export this function in order to test things in other files?
export const PRIVATE = { getActionMetadata };

const CreativeActionsCell = {
	key: "actions",
	name: "Actions",
	cellClass: "actions-cell",
	width: 150,
	formatter: CreativeActions,
	resizable: false,
	sortable: false,
	locked: true,
	getRowMetaData: getActionMetadata,
};

export default CreativeActionsCell;
